import { FC, useState, useEffect, useCallback } from 'react';

import { useAngularCallback } from 'utils/contexts/angular';
import { useExpansionContext } from 'utils/contexts/expansion';
import {
  AngularCatalogQueryContext,
  DataReturnedToAngular,
  ReturnDataToAngularFn,
} from './AngularCatalogQueryContext';
import { SHOW_CATALOG_QUERY_DRAWER_CALLBACK_KEY } from '../constants';
import { CatalogQueryDetailsAngular } from '../domain';

const isQueryEmpty = (catalogQueryDetails?: CatalogQueryDetailsAngular) =>
  !catalogQueryDetails || !catalogQueryDetails.searchField;

export const AngularCatalogQueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [saveCatalogQuery, captureSaveCallback] = useState<ReturnDataToAngularFn>();
  const [catalogQueryDetails, captureCatalogQueryDetails] = useState<CatalogQueryDetailsAngular>();
  const { expand, collapse } = useExpansionContext();

  useAngularCallback(
    SHOW_CATALOG_QUERY_DRAWER_CALLBACK_KEY,
    (details: CatalogQueryDetailsAngular) => {
      // Capture data sent from Angular
      captureCatalogQueryDetails(details);

      // Return a promise to Angular that resolves on save w/ new catalog query data
      return new Promise<DataReturnedToAngular>((save) => captureSaveCallback(() => save));
    },
  );

  const saveAndClose = useCallback(
    (data: DataReturnedToAngular) => {
      // Return data back to Angular
      if (saveCatalogQuery) saveCatalogQuery(data);

      // Clean up state
      captureCatalogQueryDetails(undefined);
      captureSaveCallback(undefined);
    },
    [saveCatalogQuery],
  );

  useEffect(() => {
    // Expand drawer when data is available, collapse it when data is cleared
    if (!isQueryEmpty(catalogQueryDetails)) expand();
    else collapse();
  }, [catalogQueryDetails]);

  return (
    <AngularCatalogQueryContext.Provider
      value={{
        catalogQueryDetails,
        saveAndClose,
      }}
    >
      {children}
    </AngularCatalogQueryContext.Provider>
  );
};
